/**
 * Reading an ABSA Cheque Account CSV export.
 *
 * Measured against the owner's real export of 29 Jul – 29 Aug 2026 (339 rows,
 * supplied 2026-08-29). That file is live financial data and is NOT in this
 * repository; the fixtures beside this module mimic its grammar instead.
 *
 * ---------------------------------------------------------------------------
 * WHY THE PARSER IS IN THE BROWSER, AND IN `lib/`
 * ---------------------------------------------------------------------------
 *
 * Decision 4 of docs/bank-reconciliation-milestone-plan.md: the browser parses,
 * the server takes JSON. `stock-imports` already expects the client to have
 * turned a spreadsheet into JSON, there is no CSV parser and no multipart
 * handler anywhere in api/src (slips.js:34 records choosing base64-in-JSON over
 * adding one), and this file is the reason that stays true.
 *
 * And it is in `lib/` and not beside a component for the reason views.js and
 * cashBookGrid.js both state at length: `npm test -w web` is node's own runner
 * over `src/lib/*.test.js`, and node cannot parse JSX. A parser that lived in a
 * component would be a parser this repository cannot run.
 *
 * ---------------------------------------------------------------------------
 * THE FOUR THINGS THIS FILE EXISTS TO GET RIGHT
 * ---------------------------------------------------------------------------
 *
 *  1. TWO DECIMAL CONVENTIONS LIVE IN ONE FILE. The `Amount` and `Balance`
 *     COLUMNS use a decimal point — `-364.00`, `192036.67`. The per-transaction
 *     fees printed INSIDE the description use the South African decimal comma —
 *     `( 8,00 )`, `( 1,25 )`. A parser that handles "the" separator corrupts one
 *     of them. So: the columns are parsed, strictly, with a point; the narrative
 *     is NEVER parsed as a number. `parseStatementAmount` refuses a comma by
 *     name rather than guessing which convention it was looking at, and
 *     `normaliseStatementDescription` strips the bracketed fees outright so that
 *     a comma-decimal number cannot survive as a token for anything downstream
 *     to be tempted by. The owner settled 2026-08-29 that these fees are
 *     informational: they arrive later as four consolidated rows
 *     (`TRANSACTION CHARGE`, `ADMIN CHARGE`, `MONTHLY ACC FEE`,
 *     `COMMITMENT FEE`) which are matched as ordinary statement lines.
 *
 *  2. THE RUNNING BALANCE IS THE FILE'S OWN PROOF OF COMPLETENESS. The real
 *     export has ZERO continuity breaks across all 339 rows. So the chain is
 *     VERIFIED, not trusted: `balance[n] === balance[n-1] + amount[n]` over
 *     every kept row, and a file with a gap is refused. This is the single
 *     strongest integrity check available on a bank statement and it is free.
 *     The server re-verifies it (decision 5) — trusting a client-side parser for
 *     an integrity proof would make the proof worthless.
 *
 *  3. ZERO-AMOUNT ROWS ARE SKIPPED, AND SKIPPED FIRST. 65 of the real file's
 *     339 rows carry `0.00` (58 of them `PROOF OF PMT EMAIL ( 1,25 )`, plus
 *     stamped statements and SMS notices). The owner settled 2026-08-29 that
 *     they are dropped entirely. Verified safe, and the design spec (§6.6)
 *     fixes the ORDER: skip first, then verify the chain over what remains, so
 *     the stored statement proves ITS OWN completeness rather than some other
 *     document's. A zero amount cannot move a balance, which is why 339 → 274
 *     rows still has zero breaks and the same closing balance. There is a test
 *     that asserts exactly that property against a fixture rather than leaving
 *     it as a claim in a comment.
 *
 *  4. EVERY REFUSAL, NEVER THE FIRST — WITH THE ROW NUMBER THE USER SEES.
 *     Decision 7, and stockImport.js:29 says why: *"a user fixing a
 *     four-hundred-row spreadsheet one error per upload gives up, and is right
 *     to."* So the row pass collects every fault, each carrying `row` = the
 *     1-based line number in the file as opened in a spreadsheet — counting the
 *     header, counting blank lines, counting the zero-amount rows that never
 *     become statement lines. An array index would be a different number and
 *     would send the user to the wrong line.
 *
 * ---------------------------------------------------------------------------
 * TWO ROW NUMBERS, AND BOTH ARE NEEDED
 * ---------------------------------------------------------------------------
 *
 *  - `rowOrdinal` runs 1..N over the KEPT rows. It is the statement line's
 *    identity — `UNIQUE (statement_id, row_ordinal)` — because the plan
 *    measured that a statement line has no natural key: 53 rows of the real
 *    file share a `(date, amount, description)` triple, eleven identical
 *    `PROOF OF PMT EMAIL` on 30 July alone. Dedup on content silently drops
 *    real lines.
 *  - `sourceRowNo` is the number the user sees. After commit, when somebody
 *    rings up about "row 212", that number is not recoverable from
 *    `row_ordinal` — 274 kept rows against 339 file rows plus a header. The
 *    design spec (§6.6) makes it a column for exactly this reason.
 *
 * ---------------------------------------------------------------------------
 * MONEY IS INTEGER CENTS, AND NEVER A FLOAT
 * ---------------------------------------------------------------------------
 *
 * `api/src/lib/money.js` states the convention: cents, as BigInt in JS and
 * BIGINT in Postgres, because `0.1 + 0.2 !== 0.3` is a curiosity in most
 * software and a misstatement in a ledger. This module holds the browser's own
 * small copy of `toCents`'s grammar rather than importing across the workspace
 * boundary — web and api are separate npm workspaces and web has never depended
 * on api's source. It is NOT a general money helper and must not become one:
 * `web/src/lib/` deliberately has no cents arithmetic at all (cashBookGrid.js's
 * header says so, and api.js's), and the single reason this file is the
 * exception is that a bank statement's own arithmetic — the balance chain — is
 * the integrity proof and has to happen where the file is read.
 *
 * The parsed figures leave here as DECIMAL STRINGS OF INTEGER CENTS
 * (`'-36400'`), never as JS numbers and never as BigInt: `JSON.stringify`
 * throws on a BigInt, and this object's whole purpose is to be posted. That is
 * also exactly how the server already sends money to the browser — api.js:
 * *"the server sends both `*_cents` (a string, because it is a BIGINT) and a
 * formatted decimal string"*.
 */

/** The header the real export writes, and the only one accepted. */
export const STATEMENT_HEADER = ['Date', 'Description', 'Amount', 'Balance'];

// ---------------------------------------------------------------- the CSV ---

/**
 * Split CSV text into records, each carrying the file line it started on.
 *
 * Written by hand, and small enough to be: this project has consistently
 * declined to take a dependency for something this size, and a CSV reader that
 * handles quotes, CRLF and a BOM is forty lines.
 *
 * Four things measured off the real file, each of which breaks a naive
 * `split(',')`:
 *
 *  - A UTF-8 BOM. Python needed `utf-8-sig` to read it; here that is one
 *    `charCodeAt(0)` test. Left in place it would make the first header cell
 *    `﻿Date` and the file would be refused as "not an ABSA export".
 *  - CRLF line endings.
 *  - Quoted descriptions containing commas — every description in the file is
 *    quoted, and the fee inserts `( 8,00 )` put commas inside them.
 *  - A trailing newline, which must not produce a final empty record.
 *
 * A newline INSIDE a quoted field is handled rather than refused; the record's
 * `sourceRowNo` is then the physical line it began on. The real export has
 * none, and a spreadsheet would show such a record as one row, so the two
 * numberings could differ — for a file that does not exist. Refusing valid CSV
 * to avoid a hypothetical off-by-one is the worse trade.
 */
export function parseCsvRows(text) {
  let s = String(text ?? '');
  if (s.charCodeAt(0) === 0xfeff) s = s.slice(1);

  const records = [];
  let fields = [];
  let field = '';
  let quoted = false;
  let started = false;      // has anything of this record been seen?
  let line = 1;             // the physical line number, 1-based
  let recordLine = 1;       // the line this record began on

  const beginIfNeeded = () => {
    if (!started) { started = true; recordLine = line; }
  };
  const endField = () => { fields.push(field); field = ''; };
  const endRecord = () => {
    endField();
    records.push({ sourceRowNo: recordLine, fields });
    fields = [];
    started = false;
  };

  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];

    if (quoted) {
      if (c === '"') {
        // A doubled quote is one literal quote; a single one closes the field.
        if (s[i + 1] === '"') { field += '"'; i += 1; } else { quoted = false; }
      } else {
        if (c === '\n') line += 1;
        field += c;
      }
      continue;
    }

    if (c === '"') { beginIfNeeded(); quoted = true; continue; }
    if (c === ',') { beginIfNeeded(); endField(); continue; }
    if (c === '\r' || c === '\n') {
      if (c === '\r' && s[i + 1] === '\n') i += 1;
      // A record exists if anything at all was seen on the line, INCLUDING a
      // bare comma: ",,," is three empty fields, not nothing.
      if (started || field !== '' || fields.length > 0) endRecord();
      line += 1;
      continue;
    }
    beginIfNeeded();
    field += c;
  }

  // No trailing newline: the last record is still a record.
  if (started || field !== '' || fields.length > 0) endRecord();

  return records;
}

/** A record with nothing in any cell — a blank line. It is not a row. */
const isBlankRecord = (r) => r.fields.every((f) => f.trim() === '');

// ------------------------------------------------------------ the columns ---

/**
 * `20260729` -> `'2026-07-29'`.
 *
 * The real file's `Date` column is `YYYYMMDD`. Not ISO, not `DD/MM/YYYY` — and
 * the last of those is the one that matters, because `07082026` and `08072026`
 * are both real dates a month apart, so a parser that guessed would be silently
 * wrong rather than loudly wrong. Eight digits, exactly, or it is refused.
 *
 * The output is the wire's date shape (`ledger.js`'s `DATE_SHAPE`), so nothing
 * downstream ever sees the bank's format.
 *
 * `setUTCFullYear` and not `Date.UTC`, copied deliberately from
 * `ledger.js:isRealDate`: `Date.UTC` applies JavaScript's legacy two-digit-year
 * mapping to years 0-99 even when handed an explicit four-digit one.
 *
 * Returns `{ date, problem }`; exactly one of the two is non-null.
 */
export function parseStatementDate(raw) {
  const s = String(raw ?? '').trim();
  if (s === '') return { date: null, problem: 'the date is blank' };
  if (!/^\d{8}$/.test(s)) {
    return { date: null, problem: `"${s}" is not a date — an ABSA export writes YYYYMMDD, e.g. 20260729` };
  }
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(4, 6));
  const d = Number(s.slice(6, 8));
  if (m < 1 || m > 12 || d < 1 || d > 31) {
    return { date: null, problem: `${s} is not a real date` };
  }
  const dt = new Date(0);
  dt.setUTCFullYear(y, m - 1, d);
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) {
    return { date: null, problem: `${s} is not a real date` };
  }
  return { date: `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`, problem: null };
}

/**
 * `'-364.00'` -> `-36400n`. A decimal POINT, and nothing else.
 *
 * Deliberately STRICTER than `money.js`'s `toCents`, which accepts a leading R,
 * spaces, and a comma as either the decimal separator or a thousands separator.
 * That grammar is right for a human typing into a form and wrong here, because
 * this file contains BOTH conventions and a lenient reader cannot tell them
 * apart: `1,25` is one rand twenty-five under one convention and one hundred
 * and twenty-five rand under the other. So a comma in the Amount or Balance
 * column is refused by name, and the message says which two things it might
 * have been — rather than picking one and being silently wrong about R2 018.25
 * of fees.
 *
 * More than two decimals is refused for `toCents`'s reason: silently rounding
 * here is how half-cents disappear without a trace.
 *
 * Returns `{ cents, problem }` — `cents` is a BigInt, exactly one is non-null.
 */
export function parseStatementAmount(raw) {
  const s = String(raw ?? '').trim();
  if (s === '') return { cents: null, problem: 'the amount is blank' };
  if (s.includes(',')) {
    return {
      cents: null,
      problem: `"${s}" contains a comma — the Amount and Balance columns of an ABSA export use a decimal point, and this file also prints fees with a decimal comma, so a comma here cannot be read either way`,
    };
  }
  const m = /^([-+]?)(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (m[2] === '' && (m[3] ?? '') === '')) {
    return { cents: null, problem: `"${s}" is not an amount` };
  }
  const [, sign, whole, frac = ''] = m;
  if (frac.length > 2) {
    return { cents: null, problem: `${s} has more than 2 decimal places` };
  }
  const cents = BigInt(whole || '0') * 100n + BigInt((frac + '00').slice(0, 2));
  return { cents: sign === '-' ? -cents : cents, problem: null };
}

// -------------------------------------------------------- the description ---

/**
 * `(EFFEC 27072026)` — statement furniture, `ddmmyyyy` of it.
 *
 * COPIED CHARACTER FOR CHARACTER from `api/src/lib/bankStatement.js`, which
 * holds the reasoning and is the only definition that governs anything. Six to
 * eight digits and `\s*`, because the literal `EFFEC` has already done all of
 * the discriminating and the digit count separates nothing.
 */
const EFFEC = /\(\s*EFFEC\s*\d{6,8}\s*\)/gi;

/**
 * A bracketed per-transaction fee: `( 8,00 )`, `( 1,25 )`, `(      12,50 )`.
 *
 * Also copied character for character from `api/src/lib/bankStatement.js`. THE
 * TWO DECIMALS ARE REQUIRED: this pattern names no literal, so the
 * printed-currency shape is the only thing telling a fee from a bracketed
 * REFERENCE, and the wider `[\d.,\s]+` eats `( 4405230012 )`. `(PTY)` and
 * `(EFFEC …)` are untouched either way, because they contain letters.
 */
const BRACKETED_FEE = /\(\s*\d+[.,]\d{2}\s*\)/g;

/** Company-form suffixes, from api/src/lib/supplierName.js. */
const SUFFIXES = /\s+\(?(PTY|PROPRIETARY)\)?\s*(LIMITED|LTD)\.?$|\s+(CC|INC|LTD|LIMITED|NPC)\.?$/;

/** A trailing store or till number, from api/src/lib/supplierName.js. */
function stripTrailingNumber(s) {
  const m = /^(.+?)\s+(\d{3,})$/.exec(s);
  if (!m) return s;
  const [, prefix, digits] = m;
  const tokenCount = prefix.split(' ').filter(Boolean).length;
  return tokenCount >= 2 ? prefix : `${prefix} ${digits}`;
}

/**
 * The description, reduced to something a person can read on the preview
 * screen — and ADVISORY, which is the whole point of this comment.
 *
 * ⚠ THIS IS A PORT OF `api/src/lib/bankStatement.js`'s
 * `normaliseStatementDescription`, WHICH IS THE ONLY DEFINITION THAT GOVERNS
 * ANYTHING. It is not authoritative and nothing downstream may treat it as
 * such — `bankReconciliation.js:82` accepts `normalisedDescription` on the wire
 * and IGNORES it, and `planStatementImport` recomputes from the raw
 * description, so the value this returns is never read by anything but the
 * preview screen.
 *
 * The two definitions the SERVER had were collapsed into one, because there a
 * drift is a real fault: the value stored and the value scored were separate
 * computations, and they had already diverged. That collapse is not available
 * here — a browser module cannot import across the api/web boundary — so this
 * stays a third copy, and its regexes above are copied character for character
 * with a pointer back rather than reasoned out again.
 *
 * The design spec settles why that is safe (§1.4): the server recomputes
 * `normalised_description` with the real `normaliseSupplier` before storing it,
 * for the same reason decision 5 has the server re-verify a balance chain it
 * did not parse. A port is a second copy of a matching rule, and decision 9
 * records that drift happening for real one directory over —
 * `SlipReview.jsx:236-245`, where a client-side re-derivation "drifted from the
 * real matching in ways that mattered". So this port's only job is making the
 * preview readable, and a drift between it and the server costs nothing.
 *
 * Two strips come FIRST that belong to bank statements and not to till slips:
 *
 *  - `(EFFEC ddmmyyyy)`. Statement furniture — eight digits when the bank
 *    zero-pads, so leaving it in would let a DATE satisfy the matcher's
 *    shared-reference test on every row that carries one.
 *  - The bracketed per-transaction fees, and ONLY the ones printed with two
 *    decimals. Stripping those makes it STRUCTURALLY IMPOSSIBLE for a
 *    comma-decimal number to reach the matcher as a token, rather than relying
 *    on every future reader knowing not to parse it; stripping a bracketed run
 *    WITHOUT decimals would instead delete a reference, which is evidence.
 *
 * What follows is `normaliseSupplier`'s own body. `chainToken` is deliberately
 * NOT applied and must not be: its premise is that the chain name leads, which
 * is true of a till slip and false of a bank statement — the plan measured 121
 * distinct leading "transaction types" precisely because the payee bleeds into
 * them, so `chainToken('ACB CREDIT ELEGANCE JEWELLERS')` is `ACB`, shared with
 * hundreds of unrelated rows.
 */
export function normaliseStatementDescription(raw) {
  if (typeof raw !== 'string') return '';

  let s = raw.replace(EFFEC, ' ').replace(BRACKETED_FEE, ' ');

  s = s.toUpperCase()
    .replace(/[.,'"`]/g, '')
    .replace(/[^A-Z0-9()\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  let previous;
  do {
    previous = s;
    s = s.replace(SUFFIXES, '').trim();
  } while (s !== previous);

  return stripTrailingNumber(s);
}

// ------------------------------------------------------------ the refusals --

/**
 * One collected refusal, in the shape `stockImport.js`'s `Refusals` uses.
 *
 * `row` is THE ROW NUMBER THE USER SEES — the line in the file as a spreadsheet
 * opens it, header included, blank lines included, skipped zero-amount rows
 * included — and is null only for a fault about the file as a whole. The
 * message repeats it so a flat list of errors reads correctly on its own, in a
 * log or a support email, without the surrounding structure.
 */
const refusal = (row, code, message) => ({
  row,
  code,
  message: row == null ? `this file: ${message}` : `row ${row}: ${message}`,
});

// ------------------------------------------------------------- the parser ---

/**
 * Parse an ABSA Cheque Account CSV export.
 *
 * NEVER THROWS ON BAD DATA — it returns errors. A parser that throws reports
 * one fault per upload, which is decision 7's whole objection.
 *
 * Returns:
 * ```
 * {
 *   ok,                 // true when there are no errors and at least one row
 *   rows: [{ rowOrdinal, sourceRowNo, transactionDate, description,
 *            normalisedDescription, amountCents, balanceCents }],
 *   skipped: [{ sourceRowNo, transactionDate, description }],
 *   errors: [{ row, code, message }],
 *   summary: { rowCount, skippedCount, fromDate, toDate,
 *              openingBalanceCents, closingBalanceCents },
 * }
 * ```
 *
 * `openingBalanceCents` is not a column in the file. It is
 * `balance[0] - amount[0]` of the first KEPT row; `closingBalanceCents` is the
 * last kept row's balance.
 */
export function parseAbsaStatement(text) {
  const errors = [];
  const empty = {
    ok: false, rows: [], skipped: [], errors,
    summary: {
      rowCount: 0, skippedCount: 0, fromDate: null, toDate: null,
      openingBalanceCents: null, closingBalanceCents: null,
    },
  };

  const records = parseCsvRows(text).filter((r) => !isBlankRecord(r));
  if (records.length === 0) {
    errors.push(refusal(null, 'EMPTY_FILE', 'there is nothing in it'));
    return empty;
  }

  // --- the header -----------------------------------------------------------
  const header = records[0];
  const got = header.fields.map((f) => f.trim());
  const matches = got.length === STATEMENT_HEADER.length
    && got.every((f, i) => f.toLowerCase() === STATEMENT_HEADER[i].toLowerCase());
  if (!matches) {
    errors.push(refusal(
      header.sourceRowNo, 'HEADER_UNEXPECTED',
      `this is not an ABSA Cheque Account export — the first line should read ${STATEMENT_HEADER.join(',')}, and it reads ${got.join(',')}`,
    ));
    // Without a header there is no way to know which column is which, so no
    // row is read. Reporting 339 column errors under a wrong header would bury
    // the one fault the user can act on.
    return empty;
  }

  // --- the rows -------------------------------------------------------------
  // Every fault is collected. Nothing returns early.
  const kept = [];
  const skipped = [];

  for (const record of records.slice(1)) {
    const { sourceRowNo, fields } = record;
    if (fields.length !== STATEMENT_HEADER.length) {
      errors.push(refusal(
        sourceRowNo, 'ROW_COLUMNS',
        `expected ${STATEMENT_HEADER.length} columns and found ${fields.length}`,
      ));
      continue;
    }

    const [rawDate, rawDescription, rawAmount, rawBalance] = fields;
    const { date, problem: dateProblem } = parseStatementDate(rawDate);
    const { cents: amountCents, problem: amountProblem } = parseStatementAmount(rawAmount);
    const { cents: balanceCents, problem: balanceProblem } = parseStatementAmount(rawBalance);

    if (dateProblem) errors.push(refusal(sourceRowNo, 'DATE_INVALID', dateProblem));
    if (amountProblem) errors.push(refusal(sourceRowNo, 'AMOUNT_INVALID', `Amount: ${amountProblem}`));
    if (balanceProblem) errors.push(refusal(sourceRowNo, 'BALANCE_INVALID', `Balance: ${balanceProblem}`));
    if (dateProblem || amountProblem || balanceProblem) continue;

    // The description as printed, minus the fixed-width padding at its ends.
    // The internal padding is left alone: it is what the bank printed and it is
    // what a bookkeeper reading the preview will compare against their own PDF.
    const description = rawDescription.trim();

    // ZERO-AMOUNT ROWS ARE SKIPPED, AND SKIPPED BEFORE THE CHAIN IS CHECKED.
    // Order matters and the spec fixes it (§6.6): the stored statement has to
    // prove its OWN completeness, so the chain must hold over exactly the rows
    // that get stored. This is safe because a zero amount cannot move a
    // balance — measured on the real file, 339 rows and 274 rows both have zero
    // breaks and the same closing balance.
    if (amountCents === 0n) {
      skipped.push({ sourceRowNo, transactionDate: date, description });
      continue;
    }

    kept.push({
      rowOrdinal: kept.length + 1,
      sourceRowNo,
      transactionDate: date,
      description,
      normalisedDescription: normaliseStatementDescription(description),
      amountCents,
      balanceCents,
    });
  }

  if (errors.length > 0) {
    // The chain is arithmetic over every kept row, so it cannot be checked
    // while any row failed to parse: the rows that DID parse are no longer a
    // contiguous run, and every "break" it reported would be an artefact of the
    // fault above it rather than a fact about the file. Fix the rows, upload
    // again, and the chain is checked then.
    return { ...empty, skipped };
  }

  if (kept.length === 0) {
    errors.push(refusal(
      null, 'NO_ROWS',
      skipped.length > 0
        ? `all ${skipped.length} of its rows have an amount of zero, and a zero amount is not a transaction`
        : 'it has a header and no transactions',
    ));
    return { ...empty, skipped };
  }

  // --- the balance chain ----------------------------------------------------
  // THE FILE'S OWN PROOF THAT IT IS COMPLETE. The real export has zero breaks
  // across all 339 rows, so a break is not a rounding quirk to tolerate — it
  // means rows are missing, and a statement missing rows reconciles to a number
  // nobody can defend.
  //
  // ONLY THE FIRST BREAK IS REPORTED, and this is the one place that does not
  // report every refusal. Decision 7 is about faults the user fixes ROW BY ROW;
  // a broken chain is a single fact about the whole DOCUMENT, with a single
  // remedy — export it again, unfiltered — and every later comparison is made
  // against a balance the file has already failed to account for. A list of
  // consequential breaks would read as a list of separate problems.
  for (let i = 0; i < kept.length; i += 1) {
    if (i === 0) continue;
    const previous = kept[i - 1];
    const row = kept[i];
    const expected = previous.balanceCents + row.amountCents;
    if (expected !== row.balanceCents) {
      errors.push(refusal(
        row.sourceRowNo, 'BALANCE_CHAIN_BREAK',
        `the running balance does not follow on. Row ${previous.sourceRowNo} closes at ${rands(previous.balanceCents)} and this row moves ${rands(row.amountCents)}, which gives ${rands(expected)} — but the file says ${rands(row.balanceCents)}. Rows are missing from this export; download it again without a filter`,
      ));
      return { ...empty, skipped };
    }
  }

  const first = kept[0];
  const last = kept[kept.length - 1];
  const dates = kept.map((r) => r.transactionDate);

  return {
    ok: true,
    rows: kept.map((r) => ({ ...r, amountCents: String(r.amountCents), balanceCents: String(r.balanceCents) })),
    skipped,
    errors,
    summary: {
      rowCount: kept.length,
      skippedCount: skipped.length,
      // min/max rather than first/last. The chain proves the rows are in the
      // order the bank wrote them; it does not prove that order is by date, and
      // a period is a period whatever the row order.
      fromDate: dates.reduce((a, b) => (b < a ? b : a)),
      toDate: dates.reduce((a, b) => (b > a ? b : a)),
      openingBalanceCents: String(first.balanceCents - first.amountCents),
      closingBalanceCents: String(last.balanceCents),
    },
  };
}

/** BigInt cents as rands, for a refusal a person has to read. */
function rands(cents) {
  const neg = cents < 0n;
  const abs = neg ? -cents : cents;
  return `${neg ? '-' : ''}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
}
